/* eslint-disable security/detect-object-injection */
import {
  Tooltip,
  Title,
  ActionIcon,
  Text,
  Drawer,
  useMantineColorScheme,
  alpha,
  isLightColor,
} from "@mantine/core"
import { IconChevronRight, IconChevronDown } from "@tabler/icons-react"
import {
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useEffect,
} from "react"
import { useTranslation } from "react-i18next"
import { DateOnly, Nullable, TimeOnly } from "../../../../@client/System"
import { CreateShiftModal } from "./modals/CreateShiftModal"
import { EditShiftModal } from "./modals/EditShiftModal"
import { CreateAbsenceModal } from "./modals/CreateAbsenceModal"
import { match } from "ts-pattern"
import {
  SuspenseWithErrorBoundary,
  SuspenseWithFallback,
} from "../../../../-components/suspense"
import { ShiftTypeDto } from "../../../../@client/Theasoft_ApiServer_ShiftTypes"
import { ts_Schicht } from "../../../../@client/Theasoft_DataClasses_Perso"
import { TFunction } from "i18next"
import dayjs from "dayjs"
import { MenuCellRenderer } from "./cells/menuCellRenderer"
import { useShallow } from "zustand/react/shallow"
import { useGridStore } from "./state"
import { EditAbsenceContent } from "@/routes/app/duties/orchestra/-components/modals/edit-absence-modal"
import { AddCastModal } from "@/routes/app/duties/orchestra/-components/modals/add-cast-modal"
import { EditCastModal } from "@/routes/app/duties/orchestra/-components/modals/edit-cast-modal"
import { AddAbsenceModal } from "@/routes/app/duties/orchestra/-components/modals/add-absence-modal"
import { OrchestraActivityDto } from "@/@client/Theasoft_ApiServer_Orchestra"
import { ActivityHeaderCell } from "../../duties/orchestra/-components/cells/activity-header-cell"
import styles from "./cells/styles.module.css"
import clsx from "clsx"
import { AddPersonStatusShiftModalContent } from "../../duties/orchestra/-components/modals/add-personstatus-shift"
import {
  Cell,
  DepartmentDutyRosterResult,
  PersonStatusDto,
  DutySchedulingRowHeader as RowHeader,
} from "@/orval/gen/model"
import { FormulaCell } from "./cells/formula-cell"
import { FormulaHeaderCell } from "./cells/formula-header-cell"
import { EditAbsenceShiftModalContent } from "./modals/EditAbsenceShiftModalContent"

export type CellId = `[${string}][${string}/${number}]`

function getCellId(hierarchicalRowId: string, day: string, colId: number) {
  return `[${hierarchicalRowId}][${day}/${colId}]` as CellId
}

const getRowId = (cellId: CellId) => cellId.split("]")[0].substring(1)

const getColId = (cellId: CellId) => cellId.split("]")[1].substring(1)

function getContractId(cellId: CellId) {
  return parseInt(getRowId(cellId).split("/")[2])
}

function getDay(cellId: CellId): DateOnly {
  return getColId(cellId).split("/")[0] as DateOnly
}

const getDepartmentId = (cellId: CellId) => {
  const raw = getRowId(cellId).split("/")[0]
  return parseInt(raw)
}

const getWorkareaId = (cellId: CellId) => {
  const raw = getRowId(cellId).split("/")[1]
  return parseInt(raw)
}

const cellSizes = { sm: [20, 20] } as const

export type DutyRosterSheetCellMenuRenderer = (
  cell?: Cell
) => ReactElement | null

export type DutyRosterSheetCellRenderer = (
  containerProps: any,
  cell: Cell | undefined,
  cellId: CellId,
  ctx: DepartmentDutyRosterSpreadsheetContext,
  rowHeader: RowHeader
) => ReactElement

type AddShiftModalState = {
  view: "add-shift"
  cellId: CellId
  shiftType: ShiftTypeDto
}

type EditShiftModalState = {
  view: "edit-shift"
  cellId: CellId
  dutyRosterId: number
  shiftId: ts_Schicht
}

type EditAbsenceShiftModalState = {
  view: "edit-absence-shift"
  cellId: CellId
  dutyRosterId: number
  shiftId: ts_Schicht
}

type AddAbsenceModalState = {
  view: "add-absence"
  cellId: CellId
  shiftType: ShiftTypeDto
}

type AddPersonStatusAbsenceModalState = {
  view: "add-personstatus-absence"
  cellId: CellId
  shiftType: ShiftTypeDto
}

type AddPersonStatusShiftModalState = {
  view: "add-personstatus-shift"
  cellId: CellId
  shiftTypeId: number
}

type EditPersonStatusAbsenceModalState = {
  view: "edit-personstatus-absence"
  cellId: CellId
  personStatus: PersonStatusDto
}

type EditAbsenceModalState = {
  view: "edit-absence"
  cellId: CellId
  personStatus: PersonStatusDto
}

type AddCast = {
  view: "add-cast"
  cellId: CellId
  roleId: number
}

type EditCast = {
  view: "edit-cast"
  cellId: CellId
  castId: number
}
// move to state

export type ModalState =
  | AddShiftModalState
  | EditShiftModalState
  | EditAbsenceShiftModalState
  | AddAbsenceModalState
  | EditAbsenceModalState
  | AddPersonStatusAbsenceModalState
  | AddPersonStatusShiftModalState
  | EditPersonStatusAbsenceModalState
  | AddCast
  | EditCast

type UseStateResult<S> = [S, Dispatch<SetStateAction<S>>]

export type DepartmentDutyRosterSpreadsheetContext = {
  t: TFunction<"app_server", undefined>
  activeMenuState: UseStateResult<CellId | null>
  mouseOverState: UseStateResult<CellId | null>
  modalState: UseStateResult<ModalState | null>
  setEditAbsence: (cellId: CellId, personStatusId: number) => void
  addShifts: (
    cellId: CellId,
    shiftType: ShiftTypeDto,
    startTime: Nullable<TimeOnly>,
    endTime: Nullable<TimeOnly>,
    note: string | null
  ) => void
  getActivity: (cellId: CellId) => OrchestraActivityDto | null
  setAddCast: (cellId: CellId, roleId: number) => void
  setEditCast: (cellId: CellId, castId: number) => void
}

export function DepartmentDutyRosterSpreadsheet({
  focus,
  title,
  data: items,
  onRefetch,
  hoverEffect = true,
  departmentId,
}: {
  focus: "shift-planning" | "cast-planning" | "absence-planning"
  title?: ReactNode
  data: DepartmentDutyRosterResult
  onRefetch?: () => void
  hoverEffect?: boolean
  departmentId?: number | undefined
}) {
  const { rowExpandedStates, setRowExpandedStates, ...store } = useGridStore(
    useShallow((s) => ({
      headerColumnCount: s.headerColumnCount,
      formulas: s.formulas,
      setContext: s.setContext,
      columns: s.columns,
      gridTemplateColumns: s.gridTemplateColumns,
      rowExpandedStates: s.rowExpandedStates,
      setRowExpandedStates: s.setRowExpandedStates,
    }))
  )

  const { t } = useTranslation("app_server")

  useEffect(() => {
    const m: Record<string, boolean> = {}
    function createKeys(row: RowHeader, parentId: string | null) {
      const rowId = parentId ? `${parentId}/${row.id}` : row.id!.toString()
      m[rowId] = row.opened ?? false
      ;(row.children as RowHeader[])?.forEach((v) => createKeys(v, rowId))
    }
    items.rowHeaders!.forEach((v) => createKeys(v, null))
    setRowExpandedStates(m)
  }, [items])

  const [headerSize, _] = focus === "cast-planning" ? [20, 68] : cellSizes["sm"]

  const gridTemplateColumns = store.gridTemplateColumns
  const rowSizes = getRowHeights()
  const gridTemplateRows = `${headerSize}px ${headerSize}px ${headerSize}px ${items.showActivityHeader ? "72px" : "0px"} ${rowSizes.join(" ")}`

  function getRowHeights() {
    let sizes: string[] = []
    function addSizes(
      row: RowHeader,
      parentId: string | null,
      expanded: boolean
    ) {
      sizes.push(expanded ? `${headerSize}px` : "0")
      if (row.children && row.children.length > 0) {
        const id = parentId ? `${parentId}/${row!.id}` : row.id!.toString()
        const opened = rowExpandedStates[id]
        ;(row.children as RowHeader[])?.forEach((v) => {
          addSizes(v, id, expanded && opened)
        })
      }
    }
    items.rowHeaders!.forEach((v) => {
      addSizes(v, null, true)
    })
    return sizes
  }

  const { colorScheme } = useMantineColorScheme()

  const maxDepth = store.headerColumnCount + 1

  // TODO: remove magic numbers
  const colHeaderHeight = 4
  const columnHeaderCells: any = []
  for (let i = 0; i < store.columns.length; i++) {
    const c = store.columns[i]
    match(c)
      .with({ type: "month" }, (x) => {
        columnHeaderCells.push(
          <div
            className={clsx(styles["cell"], styles["header"])}
            style={{
              top: 0,
              gridColumn: `${i + maxDepth + 2 + (i === 0 ? +1 : 0)} / span ${x.span - 2}`,
              gridRow: 1,
            }}
          >
            <Text size="xs">{dayjs(x.date).format("MMM")}</Text>
          </div>
        )
      })
      .with({ type: "day" }, (x) => {
        const isLightBackgroundColor = x.bg && isLightColor(x.bg)
        columnHeaderCells.push(
          <div
            className={clsx(styles["cell"], styles["header"])}
            style={{
              top: 36,
              background:
                colorScheme === "dark" && x.bg ? alpha(x.bg, 0.7) : x.bg,
              gridColumn: `${i + maxDepth + 1} / span ${x.span - 1}`,
              gridRow: 3,
            }}
          >
            <Text size="xs" c={isLightBackgroundColor ? "black" : undefined}>
              {focus === "absence-planning"
                ? dayjs(x.date).format("DD")
                : dayjs(x.date).format("dd DD")}
            </Text>
          </div>
        )
        columnHeaderCells.push(
          <div
            style={{
              gridColumn: `${i + maxDepth} `,
              gridRow: 4,
              background:
                "ligh-dark(var(--mantine-color-gray-8),var(--mantine-color-dark-4))",
            }}
          ></div>
        )
      })
      .with({ type: "day-col" }, (x) =>
        columnHeaderCells.push(
          <div
            className={clsx(styles["header"], styles["activity-cell"])}
            style={{
              top: 54,
              textAlign: "start",
              gridColumn: `${i + maxDepth} / span 1`,
              gridRow: 4,
              overflow: "hidden",
            }}
          >
            {x.activity ? (
              <SuspenseWithErrorBoundary>
                <ActivityHeaderCell
                  // TODO: remove/fix as any
                  activity={x.activity as any}
                  departmentId={departmentId!}
                />
              </SuspenseWithErrorBoundary>
            ) : null}
          </div>
        )
      )
      .with({ type: "week" }, (x) => {
        columnHeaderCells.push(
          <div
            className={clsx(
              styles["cell"],
              styles["header"],
              dayjs(x.date).isoWeek() % 2 === 0 && styles["header-alt"]
            )}
            style={{
              top: 18,
              gridRow: 2,
              gridColumn: `${i + maxDepth + 2} / span ${x.span - 2}`,
            }}
          >
            <Text size="xs">KW {dayjs(x.date).isoWeek()}</Text>
          </div>
        )
        columnHeaderCells.push(
          <div
            style={{
              gridColumn: `${i + maxDepth}`,
              gridRow: 3,
            }}
          />
        )
      })
  }

  const rowHeaderCells: React.ReactElement[] = []
  const rowCells: React.ReactElement[] = []

  const offset = store.headerColumnCount - store.formulas.length + 1
  for (let col = 0; col < store.formulas.length; col++) {
    rowHeaderCells.push(
      <FormulaHeaderCell
        formula={store.formulas[col]}
        style={{
          gridRow: `1 / span ${colHeaderHeight}`,
          gridColumn: `${offset + col} / span 1`,
          //TODO: replace magic numbers/parameterize
          left: 110 + col * 20,
        }}
      />
    )
  }

  function recCreateRowheaders(
    header: RowHeader,
    parentHierarchicalId: string | null,
    gridRow: number,
    depth: number = 0
  ) {
    const hierarchicalRowId =
      parentHierarchicalId !== null
        ? parentHierarchicalId + "/" + header.id!
        : header.id!.toString()

    const expanded = rowExpandedStates[hierarchicalRowId] ?? true
    rowHeaderCells.push(
      <div
        id={hierarchicalRowId || ""}
        key={hierarchicalRowId || ""}
        className={clsx(
          styles["cell"],
          styles["header"],
          header.rowType === "Contract" && styles["row-header-contract"],
          header.rowType !== "Contract" && styles["row-header"],
          header.highlight && styles["header-highlighted"]
          // hierarchicalRowId == currentRow && styles["header-hover"]
        )}
        style={{
          gridRow,
          gridColumn: `${depth + 1} / span ${header.depth! + 1 + (header.children?.length === 0 ? 1 : 0)}`,
          left: depth * 5,
        }}
      >
        <ActionIcon
          style={{
            visibility:
              header!.children && header.children.length > 0
                ? undefined
                : "hidden",
          }}
          size="xs"
          variant="subtle"
          color="gray"
          onMouseDown={() => {
            setRowExpandedStates((prev) => ({
              ...prev,
              [hierarchicalRowId]: !expanded,
            }))
          }}
        >
          {expanded ? <IconChevronDown /> : <IconChevronRight />}
        </ActionIcon>
        <Tooltip
          label={header.tooltip}
          events={{
            hover: Boolean(header.tooltip),
            focus: false,
            touch: false,
          }}
        >
          <Text size="xs" style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
            {header.label?.startsWith("@")
              ? t(header.label.substring(1) as any)
              : header.label}
          </Text>
        </Tooltip>
      </div>
    )

    const offset = store.headerColumnCount - store.formulas.length + 1
    for (let col = 0; col < store.formulas.length; col++) {
      const contractId =
        header.rowType === "Contract" ? (header.id ?? null) : null
      store.formulas[col]
      rowHeaderCells.push(
        <FormulaCell
          formula={store.formulas[col]}
          contractId={contractId}
          className={clsx(styles["cell"])}
          style={{
            position: "sticky",
            //TODO: replace magic numbers/parameterize
            left: 110 + col * 20,
            gridRow,
            gridColumn: `${offset + col} / span 1`,
          }}
        />
      )
    }

    for (let col = 0; col < store.columns.length; col++) {
      const c = store.columns[col]
      match(c).with({ type: "day-col" }, (x) => {
        const day = x.date
        const colId = x.colId
        const cellId = getCellId(hierarchicalRowId, day!, colId)
        const cell = items.cellsById![cellId]
        const props = {
          row: gridRow,
          col,
          id: cellId,
          className: clsx(styles["cell"]),
          style: {
            gridRow,
            // 2 is probably wrong here
            gridColumn: `${maxDepth + 0 + col} / span ${cell?.span ?? 1}`,
          },
        }
        rowCells.push(
          <MenuCellRenderer
            key={cellId}
            containerProps={props}
            cell={cell}
            cellId={cellId}
            header={header}
          />
        )
      })
    }

    let row = gridRow + 1

    if (expanded) {
      ;(header.children as RowHeader[])?.forEach((v) => {
        recCreateRowheaders(v, hierarchicalRowId, row, depth + 1)
        row += v.span!
      })
    }
  }
  let row = colHeaderHeight + 1

  items.rowHeaders!.forEach((v) => {
    recCreateRowheaders(v, null, row, 0)
    row += v.span!
  })

  const start = 5
  const height = items.rowCount

  const overlayAreas = items.overlays?.map((overlay, _) => {
    const { area } = overlay
    const fromI = store.columns.findIndex(
      (v) => v.type === "day" && v.date === overlay.area.from
    )
    const untilI = store.columns.findIndex(
      (v) => v.type === "day" && v.date === overlay.area.until
    )
    const width = untilI - fromI + 1
    return (
      <div>
        <div
          style={{
            gridColumn: ` ${fromI + start} / span ${width} `,
            gridRow: area.rowNumber
              ? start + area.rowNumber
              : `${start} / span ${height}`,

            pointerEvents: "none",
            zIndex: 10,
            ...(overlay.style === "Dimmed"
              ? {
                  background:
                    "light-dark(var(--mantine-color-gray-4),var(--mantine-color-dark-8))",
                  opacity: 0.5,
                }
              : overlay.style === "Highlighted"
                ? {
                    border:
                      " 2px dashed light-dark(var(--mantine-color-blue-8),var(--mantine-color-white))",
                  }
                : undefined),
          }}
        />
      </div>
    )
  })

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns,
        gridTemplateRows,
        // this is important, hidden rows get height 0. if we would allow gaps then the hidden rows would also take space
        gap: 0,
        position: "relative",
        width: "max-content",
      }}
      className={clsx(hoverEffect && styles["hover-enabled"])}
    >
      <div
        style={{
          gridColumn: `1 / span ${maxDepth - store.formulas.length - 1}`,
          gridRow: `1 / span ${colHeaderHeight}`,
          position: "sticky",
          background:
            "light-dark(var(--mantine-color-white),var(--mantine-color-dark-7))",
          top: 0,
          left: 0,
          zIndex: 199,
        }}
      >
        {title}
      </div>

      {columnHeaderCells}

      {rowHeaderCells}

      <ContentCells>{rowCells}</ContentCells>

      {overlayAreas}

      <Modals onRefetch={onRefetch} />
    </div>
  )
}

const ContentCells = React.memo(
  ({ children }: { children: React.ReactNode[] }) => {
    const { window } = useGridStore(useShallow((s) => ({ window: s.window })))

    const filteredArray = children.filter((x: any) => {
      const colIndex = x?.props?.containerProps?.col
      const row = x?.props?.containerProps?.row
      const render =
        row >= window.startRow &&
        row <= window.endRow &&
        colIndex >= window.startCol - 2 &&
        colIndex <= window.endCol
      return render
    })

    return filteredArray
  }
)

function Modals({ onRefetch }: { onRefetch?: () => void }) {
  const { t } = useTranslation()

  const { modal, setModal, getActivity } = useGridStore(
    useShallow((s) => ({
      modal: s.modalState,
      setModal: s.setModalState,
      getActivity: s.getActivity,
    }))
  )

  return (
    <Drawer
      opened={modal !== null}
      onClose={() => {
        setModal(null)
      }}
      title={
        <Tooltip label={modal?.view ?? ""}>
          <Title order={3}>
            {modal
              ? match(modal.view)
                  .with("add-absence", () => t("EnterAbsence"))
                  .with("add-personstatus-absence", () => t("EnterAbsence"))
                  .with("add-shift", () => t("AddShift"))
                  .with("edit-personstatus-absence", () => t("Absence"))
                  .with("edit-absence", () => t("Absence"))
                  .with("edit-absence-shift", () => t("Absence"))
                  .with("edit-shift", () => t("EditShift"))
                  .with("add-cast", () => t("AddCast"))
                  .with("edit-cast", () => t("EditCast"))
                  .with("add-personstatus-shift", () => t("AddShift"))
                  .exhaustive()
              : null}
          </Title>
        </Tooltip>
      }
      position="right"
      transitionProps={{ duration: 150 }}
      overlayProps={{ backgroundOpacity: 0.2, blur: 0 }}
    >
      <SuspenseWithFallback>
        {modal?.view === "add-personstatus-shift" ? (
          <AddPersonStatusShiftModalContent
            departmentId={getDepartmentId(modal.cellId)}
            shiftTypeId={modal.shiftTypeId}
            date={getDay(modal.cellId)}
            contractId={getContractId(modal.cellId)}
            onCancel={() => setModal(null)}
            onAdded={() => {
              onRefetch && onRefetch()
              setModal(null)
            }}
          />
        ) : null}
        {modal?.view === "add-absence" ? (
          <CreateAbsenceModal
            departmentId={getDepartmentId(modal.cellId)}
            shiftType={modal.shiftType}
            initialData={{
              shiftTypeId: null,
              startTime: null,
              endTime: null,
              range: [
                new Date(getDay(modal.cellId)),
                new Date(getDay(modal.cellId)),
              ],
              contractId: getContractId(modal.cellId),
              note: "",
            }}
            onCancel={() => setModal(null)}
            onAdded={() => {
              onRefetch && onRefetch()
              setModal(null)
            }}
          />
        ) : null}
        {modal?.view === "add-personstatus-absence" ? (
          <AddAbsenceModal
            coordinate={{
              type: "absence-cell",
              contractId: getContractId(modal.cellId),
              shiftTypeId: modal.shiftType.shiftTypeId,
              date: getDay(modal.cellId),
            }}
            departmentId={getDepartmentId(modal.cellId)}
            onAdded={() => {
              onRefetch && onRefetch()
              setModal(null)
            }}
            onCancel={() => setModal(null)}
          />
        ) : null}
        {modal?.view === "add-shift" ? (
          <CreateShiftModal
            shiftType={modal.shiftType}
            initialData={{
              date: getDay(modal.cellId),
              contractId: getContractId(modal.cellId),
              startTime: null,
              endTime: null,
              note: "",
            }}
            onCancel={() => setModal(null)}
            onAdded={() => {
              onRefetch && onRefetch()
            }}
            onClose={() => {
              setModal(null)
            }}
          />
        ) : null}
        {modal?.view === "edit-shift" ? (
          <EditShiftModal
            departmentId={getDepartmentId(modal.cellId)}
            date={getDay(modal.cellId)}
            contractId={getContractId(modal.cellId)}
            key={modal.view}
            dutyRosterId={modal.dutyRosterId}
            shiftId={modal.shiftId}
            onEdited={() => {
              setModal(null)
              onRefetch && onRefetch()
            }}
            onCancel={() => {
              setModal(null)
            }}
          />
        ) : null}
        {modal?.view === "edit-absence-shift" ? (
          <EditAbsenceShiftModalContent
            departmentId={getDepartmentId(modal.cellId)}
            date={getDay(modal.cellId)}
            contractId={getContractId(modal.cellId)}
            key={modal.view}
            dutyRosterId={modal.dutyRosterId}
            shiftId={modal.shiftId}
            onEdited={() => {
              setModal(null)
              onRefetch && onRefetch()
            }}
            onCancel={() => {
              setModal(null)
            }}
          />
        ) : null}
        {modal?.view === "edit-personstatus-absence" ? (
          <EditAbsenceContent
            departmentId={getDepartmentId(modal.cellId)}
            // TODO
            personStatus={
              {
                ...modal.personStatus,
                startDate: modal.personStatus.startDate,
                endDate: modal.personStatus.endDate,
                contractId: getContractId(modal.cellId),
                personId: modal.personStatus.personId!,
                personStatusId: modal.personStatus.personStatusId!,
                startTime: modal.personStatus.startTime || null,
                endTime: modal.personStatus.endTime || null,
                shiftTypeId: modal.personStatus.shiftTypeId!,
                statusDisplayName: modal.personStatus.statusDisplayName!,
                statusTypPrintColors: null,
                note: modal.personStatus.note ?? "",
              } as any
            }
            onEdited={() => {
              setModal(null)
              onRefetch && onRefetch()
            }}
            onCancel={() => {
              setModal(null)
            }}
          />
        ) : null}
        {modal?.view === "add-cast" ? (
          <AddCastModal
            departmentId={getDepartmentId(modal.cellId)}
            coordinate={{
              type: "activity-cell",
              contractId: getContractId(modal.cellId),
              activityId: getActivity(modal.cellId)?.activityId!,
              workareaId: getWorkareaId(modal.cellId),
            }}
            onAdded={() => {
              onRefetch && onRefetch()
              setModal(null)
            }}
            onCancel={() => {
              setModal(null)
            }}
          />
        ) : null}
        {modal?.view === "edit-cast" ? (
          <EditCastModal
            coordinate={{
              type: "edit",
              contractId: getContractId(modal.cellId),
              activityId: getActivity(modal.cellId)?.activityId!,
              workareaId: getWorkareaId(modal.cellId),
              castId: modal.castId,
            }}
            onCancel={() => {
              setModal(null)
            }}
            onDeleted={() => {
              setModal(null)
              onRefetch && onRefetch()
            }}
            onEdited={() => {
              setModal(null)
              onRefetch && onRefetch()
            }}
          />
        ) : null}
      </SuspenseWithFallback>
    </Drawer>
  )
}
